
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { Database } from "@/integrations/supabase/types";
import { CalendarDays, Grid2X2, LayoutGrid, Link } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { getPreviewUrl } from "@/utils/imageUtils";

type Collection = Database['public']['Tables']['collections']['Row'] & {
  creator: {
    name: string;
    surname: string;
  } | null;
  collection_artworks: {
    artwork: {
      artwork_id: number;
      title: string;
      preview: string;
    };
  }[];
};

interface CollectionCardProps {
  collection: Collection;
}

export function CollectionCard({ collection }: CollectionCardProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const toggleExhibitionMode = async (enabled: boolean) => {
    const { error } = await supabase
      .from('collections')
      .update({
        exhibition_mode: enabled,
        exhibition_mode_enabled_at: enabled ? new Date().toISOString() : null
      })
      .eq('collection_id', collection.collection_id);

    if (error) {
      console.error('Error updating exhibition mode:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "You don't have permission to update this collection"
      });
      return;
    }

    // Invalidate and refetch collections data
    await queryClient.invalidateQueries({ queryKey: ['collections'] });

    toast({
      title: "Success",
      description: `Exhibition mode ${enabled ? 'enabled' : 'disabled'}`
    });
  };

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow">
      {collection.cover_artwork_id && (
        <div className="w-full h-48 bg-muted">
          <img
            src={getPreviewUrl(collection.collection_artworks.find(
              ca => ca.artwork.artwork_id === collection.cover_artwork_id
            )?.artwork.preview)}
            alt={collection.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg font-semibold">{collection.title}</CardTitle>
            <div className="flex items-center text-sm text-muted-foreground mt-1">
              <CalendarDays className="mr-1 h-4 w-4" />
              {new Date(collection.created_at || '').toLocaleDateString()}
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {collection.layout_type === 'grid' ? (
              <Grid2X2 className="h-4 w-4 text-muted-foreground" />
            ) : (
              <LayoutGrid className="h-4 w-4 text-muted-foreground" />
            )}
            <div className="flex items-center space-x-2">
              <Switch
                checked={collection.exhibition_mode || false}
                onCheckedChange={toggleExhibitionMode}
                aria-label="Toggle exhibition mode"
              />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {collection.description && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
            {collection.description}
          </p>
        )}
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Artworks in collection:</h4>
          <ul className="space-y-1">
            {collection.collection_artworks.map(({ artwork }) => (
              <li key={artwork.artwork_id} className="text-sm">
                <a
                  href={`/artworks/${artwork.artwork_id}`}
                  className="text-blue-600 hover:text-blue-800 hover:underline flex items-center"
                >
                  <Link className="h-3 w-3 mr-1" />
                  {artwork.title}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
